import React, { useEffect, useRef, useCallback, useState } from "react";
import { useEditor, clamp } from "./store";

/**
 * AudioMeter Component
 * 
 * Displays stereo audio level meters on the side of the preview pane.
 * Reads from the shared analyser nodes set up by the preview engine.
 */

// dB constants
const MIN_DB = -60;
const MAX_DB = 0;
const PEAK_HOLD_TIME = 1500; // ms to hold peak indicator
const PEAK_DECAY_RATE = 15; // dB per second decay rate

// Converts linear amplitude (0-1) to dB
function linearToDb(value) {
  if (value <= 0) return MIN_DB;
  const db = 20 * Math.log10(value);
  return clamp(db, MIN_DB, MAX_DB);
}

// Maps dB value to percentage (0-100)
function dbToPercent(db) {
  return ((db - MIN_DB) / (MAX_DB - MIN_DB)) * 100;
}

export function AudioMeter({ height, width = 40 }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const peakLRef = useRef({ value: MIN_DB, time: 0 });
  const peakRRef = useRef({ value: MIN_DB, time: 0 });
  const smoothedLRef = useRef(MIN_DB);
  const smoothedRRef = useRef(MIN_DB);
  const lastFrameRef = useRef(0);
  const [canvasHeight, setCanvasHeight] = useState(height || 200);

  // Get shared analyser nodes from store
  const analyserL = useEditor((s) => s.audioMeterAnalyserL);
  const analyserR = useEditor((s) => s.audioMeterAnalyserR);
  const audioContext = useEditor((s) => s.audioMeterContext);
  const isPlaying = useEditor((s) => s.isPlaying);
  
  // Track if audio system is ready
  const isAudioReady = !!(analyserL && analyserR && audioContext);

  // Get audio levels from analyser nodes
  const getLevels = useCallback(() => {
    if (!analyserL || !analyserR) {
      return { left: MIN_DB, right: MIN_DB };
    }

    const bufferLength = analyserL.frequencyBinCount;
    const dataArrayL = new Float32Array(bufferLength);
    const dataArrayR = new Float32Array(bufferLength);

    analyserL.getFloatTimeDomainData(dataArrayL);
    analyserR.getFloatTimeDomainData(dataArrayR);

    // Calculate RMS (Root Mean Square) for each channel
    let sumL = 0;
    let sumR = 0;
    let peakSampleL = 0;
    let peakSampleR = 0;
    
    for (let i = 0; i < bufferLength; i++) {
      sumL += dataArrayL[i] * dataArrayL[i];
      sumR += dataArrayR[i] * dataArrayR[i];
      peakSampleL = Math.max(peakSampleL, Math.abs(dataArrayL[i]));
      peakSampleR = Math.max(peakSampleR, Math.abs(dataArrayR[i]));
    }
    
    const rmsL = Math.sqrt(sumL / bufferLength);
    const rmsR = Math.sqrt(sumR / bufferLength);

    // Use a mix of RMS and peak for more responsive meters
    const levelL = rmsL * 0.7 + peakSampleL * 0.3;
    const levelR = rmsR * 0.7 + peakSampleR * 0.3;

    return {
      left: linearToDb(levelL),
      right: linearToDb(levelR),
    };
  }, [analyserL, analyserR]);

  // Draw the meters
  const drawMeters = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    const now = performance.now();
    const dt = (now - lastFrameRef.current) / 1000;
    lastFrameRef.current = now;

    // Get current levels
    let levels = { left: MIN_DB, right: MIN_DB };
    
    if (isPlaying && analyserL && analyserR) {
      levels = getLevels();
    }

    // Smooth the levels (faster attack, slower release)
    const attackFactor = 0.3;
    const releaseFactor = 0.85;
    
    if (levels.left > smoothedLRef.current) {
      smoothedLRef.current = smoothedLRef.current * (1 - attackFactor) + levels.left * attackFactor;
    } else {
      smoothedLRef.current = smoothedLRef.current * releaseFactor + levels.left * (1 - releaseFactor);
    }
    
    if (levels.right > smoothedRRef.current) {
      smoothedRRef.current = smoothedRRef.current * (1 - attackFactor) + levels.right * attackFactor;
    } else {
      smoothedRRef.current = smoothedRRef.current * releaseFactor + levels.right * (1 - releaseFactor);
    }

    // When not playing, decay levels
    if (!isPlaying) {
      const decayAmount = 30 * dt; // dB per second
      smoothedLRef.current = Math.max(MIN_DB, smoothedLRef.current - decayAmount);
      smoothedRRef.current = Math.max(MIN_DB, smoothedRRef.current - decayAmount);
    }

    const dbL = smoothedLRef.current;
    const dbR = smoothedRRef.current;

    // Update peak hold
    if (dbL > peakLRef.current.value) {
      peakLRef.current = { value: dbL, time: now };
    }
    if (dbR > peakRRef.current.value) {
      peakRRef.current = { value: dbR, time: now };
    }

    // Decay peaks after hold time
    const peakL = peakLRef.current;
    const peakR = peakRRef.current;
    if (now - peakL.time > PEAK_HOLD_TIME) {
      peakL.value = Math.max(MIN_DB, peakL.value - (PEAK_DECAY_RATE * dt));
    }
    if (now - peakR.time > PEAK_HOLD_TIME) {
      peakR.value = Math.max(MIN_DB, peakR.value - (PEAK_DECAY_RATE * dt));
    }

    // Clear canvas with dark background
    ctx.fillStyle = "#101015";
    ctx.fillRect(0, 0, w, h);

    // Meter dimensions
    const padding = 4;
    const meterGap = 3;
    const labelHeight = 14;
    const scaleWidth = 16;
    const meterWidth = Math.floor((w - padding * 2 - meterGap - scaleWidth) / 2);
    const meterHeight = h - padding * 2 - labelHeight;
    const meterTop = padding;

    // Draw scale markers on the right
    ctx.fillStyle = "#555";
    ctx.font = "10px monospace";
    ctx.textAlign = "right";
    
    const scaleMarks = [0, -6, -12, -24, -48];
    const scaleX = w - 2;
    for (const db of scaleMarks) {
      const y = meterTop + meterHeight * (1 - dbToPercent(db) / 100);
      ctx.fillText(`${db}`, scaleX, y + 3);
      
      // Draw tick marks
      ctx.fillStyle = "#333";
      ctx.fillRect(padding, y, w - padding * 2 - scaleWidth + 2, 1);
      ctx.fillStyle = "#555";
    }

    // Draw each meter
    const drawMeter = (x, level, peak, label) => {
      const levelPercent = dbToPercent(level);
      const peakPercent = dbToPercent(peak);
      const filledHeight = (levelPercent / 100) * meterHeight;
      const peakY = meterTop + meterHeight * (1 - peakPercent / 100);

      // Draw gradient meter fill (thin vertical line)
      if (filledHeight > 0) {
        const gradient = ctx.createLinearGradient(0, meterTop + meterHeight, 0, meterTop);
        gradient.addColorStop(0, "#059669");     // Emerald-600 at bottom
        gradient.addColorStop(0.5, "#10b981");   // Emerald-500
        gradient.addColorStop(0.7, "#fbbf24");   // Amber-400
        gradient.addColorStop(0.85, "#f97316");  // Orange-500
        gradient.addColorStop(0.95, "#ef4444");  // Red-500
        gradient.addColorStop(1, "#dc2626");     // Red-600 at top
        
        // Draw as a thin vertical line (3px wide, centered)
        const lineWidth = 3;
        const lineX = x + (meterWidth - lineWidth) / 2;
        ctx.fillStyle = gradient;
        ctx.fillRect(
          lineX,
          meterTop + meterHeight - filledHeight,
          lineWidth,
          filledHeight
        );
      }

      // Draw peak indicator (thin line)
      if (peak > MIN_DB + 5) {
        const peakColor = peak >= -3 ? "#ef4444" : peak >= -6 ? "#f97316" : "#fff";
        const lineWidth = 3;
        const lineX = x + (meterWidth - lineWidth) / 2;
        ctx.fillStyle = peakColor;
        ctx.fillRect(lineX, peakY - 0.5, lineWidth, 1);
      }

      // Clipping indicator at top (thin line)
      if (level >= -1) {
        const lineWidth = 3;
        const lineX = x + (meterWidth - lineWidth) / 2;
        ctx.fillStyle = "#ef4444";
        ctx.fillRect(lineX, meterTop, lineWidth, 2);
      }

      // Label
      ctx.fillStyle = "#888";
      ctx.font = "10px monospace";
      ctx.textAlign = "center";
      ctx.fillText(label, x + meterWidth / 2, h - 2);
    };

    drawMeter(padding, dbL, peakL.value, "L");
    drawMeter(padding + meterWidth + meterGap, dbR, peakR.value, "R");

    // Draw border
    // ctx.strokeStyle = "#333";
    // ctx.lineWidth = 1;
    // ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
    
    // // Show indicator if audio isn't connected yet
    // if (!isAudioReady) {
    //   ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    //   ctx.fillRect(0, 0, w, h);
    //   ctx.fillStyle = "#666";
    //   ctx.font = "8px sans-serif";
    //   ctx.textAlign = "center";
    //   ctx.fillText("Click to", w / 2, h / 2 - 6);
    //   ctx.fillText("enable", w / 2, h / 2 + 4);
    //   ctx.fillText("audio", w / 2, h / 2 + 14);
    // }
  }, [isPlaying, analyserL, analyserR, getLevels, isAudioReady]);

  // Update canvas height when height prop changes
  useEffect(() => {
    if (height !== undefined) {
      setCanvasHeight(height);
    }
  }, [height]);

  // Resize canvas when height is not specified (use container height)
  useEffect(() => {
    if (height !== undefined) return; // Fixed height, no need to resize
    
    const resizeCanvas = () => {
      const container = containerRef.current;
      if (!container) return;
      
      const containerHeight = container.clientHeight;
      if (containerHeight > 0 && canvasHeight !== containerHeight) {
        setCanvasHeight(containerHeight);
      }
    };
    
    resizeCanvas();
    
    const resizeObserver = new ResizeObserver(resizeCanvas);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [height, canvasHeight]);

  // Animation loop
  useEffect(() => {
    lastFrameRef.current = performance.now();
    
    const animate = () => {
      drawMeters();
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [drawMeters]);

  // Resume audio context on user interaction (delegate to preview engine's context)
  useEffect(() => {
    const handleInteraction = async () => {
      const ctx = useEditor.getState().audioMeterContext;
      if (ctx && ctx.state === "suspended") {
        try {
          await ctx.resume();
        } catch (e) {}
      }
    };

    document.addEventListener("click", handleInteraction);
    document.addEventListener("keydown", handleInteraction);

    return () => {
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
    };
  }, []);

  // Initialize canvas dimensions
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = width;
    canvas.height = canvasHeight;
  }, [width, canvasHeight]);

  return (
    <div 
      ref={containerRef}
      className="flex flex-col items-center select-none border-l"
      style={height === undefined ? { height: "100%" } : {}}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={canvasHeight}
        style={{ imageRendering: "crisp-edges" }}
      />
    </div>
  );
}

export default AudioMeter;
